// The Grammar Lab's rooms: the three core labs (built here) and the tools (कारक, सन्धि, समास …).
// Used by the hub page and by the "more labs" strip at the end of every lab.

import type { LucideIcon } from 'lucide-react'

import {
    AudioLines,
    BookOpenText,
    CalendarClock,
    Combine,
    Grid2x2,
    Grid3x3,
    Link2,
    Network,
    Repeat2,
    ScanSearch,
    Sprout,
    Table2,
    Users
} from 'lucide-react'

export interface LabLink {
    href: string
    /** Devanagari name, shown as the eyebrow. */
    eyebrow: string
    /** English title. */
    en: string
    desc: { hi: string, en: string }
    icon: LucideIcon
    core: boolean
}

export const LABS: LabLink[] = [
    {
        href: '/grammar/kriya',
        eyebrow: 'धातुरूपम्',
        en: 'Dhātu laboratory',
        desc: {
            hi: 'धातु, काल, पुरुष और वचन चुनिए; हर रूप बनते हुए देखिए।',
            en: 'Choose a root, tense, person and number, and watch each form being built.'
        },
        icon: Table2,
        core: true
    },
    {
        href: '/grammar/kal',
        eyebrow: 'कालाः',
        en: 'The tenses',
        desc: {
            hi: 'हर काल के सामान्य और विकल्प प्रत्यय, पुरुष और वचन के अनुसार।',
            en: 'The standard and variant endings of every tense, by person and number.'
        },
        icon: CalendarClock,
        core: true
    },
    {
        href: '/grammar/purush-vachan',
        eyebrow: 'पुरुषवचनम्',
        en: 'Person and number',
        desc: {
            hi: 'तीन पुरुष × दो वचन: हर खाने में सर्वनाम और क्रिया।',
            en: 'Three persons × two numbers: the pronoun and verb in every cell.'
        },
        icon: Grid2x2,
        core: true
    },
    {
        href: '/grammar/shabdarup',
        eyebrow: 'शब्दरूपम्',
        en: 'Declension laboratory',
        desc: {
            hi: 'हर खाने में कारक और प्रत्यय; फिर तालिका छिपाकर याद से भरिए।',
            en: 'Every cell with its role and ending, then hide the table and fill it from memory.'
        },
        icon: Grid3x3,
        core: true
    },
    {
        href: '/grammar/vibhakti',
        eyebrow: 'विभक्तयः',
        en: 'The eight cases',
        desc: {
            hi: 'आठ विभक्तियाँ: उनकी भूमिका, चिह्न और उदाहरण।',
            en: 'The eight cases with their roles, markers and examples.'
        },
        icon: BookOpenText,
        core: true
    },
    {
        href: '/grammar/sarvanam',
        eyebrow: 'सर्वनाम',
        en: 'Pronoun tables',
        desc: {
            hi: 'मैं, तुम, वह, यह, कौन — दस तालिकाएँ, सात विभक्तियाँ।',
            en: 'I, you, he, she, this, who: ten tables across seven cases.'
        },
        icon: Users,
        core: true
    },
    {
        href: '/grammar/vakya',
        eyebrow: 'वाक्यविश्लेषणम्',
        en: 'Sentence analyser',
        desc: {
            hi: 'कोई भी प्राकृत वाक्य लिखिए: हर शब्द का व्याकरण और कर्ता-क्रिया का मेल।',
            en: 'Type any Prakrit sentence: the grammar of every word, and subject and verb agreement.'
        },
        icon: ScanSearch,
        core: false
    },
    {
        href: '/grammar/karak',
        eyebrow: 'कारक / वाक्य विश्लेषण',
        en: 'Parse a sentence',
        desc: {
            hi: 'वाक्य के हर शब्द को टैप करें — कौन कर्ता, कौन कर्म।',
            en: 'Tap each word of a sentence to see who does what to whom.'
        },
        icon: Network,
        core: false
    },
    {
        href: '/grammar/sandhi',
        eyebrow: 'सन्धि',
        en: 'Joining sounds',
        desc: {
            hi: 'दो शब्द जोड़िए, या जुड़े शब्द का विच्छेद कीजिए।',
            en: 'Join two words, or split a joined word back into its parts.'
        },
        icon: Link2,
        core: false
    },
    {
        href: '/grammar/samas',
        eyebrow: 'समास',
        en: 'Compounds',
        desc: {
            hi: 'छह प्रकार के समास, हर एक का विग्रह।',
            en: 'The six kinds of compound, each with its analysis.'
        },
        icon: Combine,
        core: false
    },
    {
        href: '/grammar/kridanta',
        eyebrow: 'कृदन्त',
        en: 'Participles',
        desc: {
            hi: 'पढ़कर, पढ़ने के लिए, पढ़ता हुआ — धातु से बने रूप।',
            en: 'Having read, in order to read, reading: forms made from a root.'
        },
        icon: Sprout,
        core: false
    },
    {
        href: '/grammar/vachya',
        eyebrow: 'वाच्य एवं प्रेरणार्थक',
        en: 'Passive and causative',
        desc: {
            hi: 'पढ़ा जाता है, पढ़वाता है — वाक्य की दिशा बदलिए।',
            en: 'Is read, makes someone read: change the direction of a sentence.'
        },
        icon: Repeat2,
        core: false
    },
    {
        href: '/grammar/dhvani',
        eyebrow: 'ध्वनि-परिवर्तन',
        en: 'Sanskrit to Prakrit sounds',
        desc: {
            hi: 'धर्म से धम्म — संस्कृत शब्द प्राकृत में कैसे बदलते हैं।',
            en: 'From धर्म to धम्म: how Sanskrit words change in Prakrit.'
        },
        icon: AudioLines,
        core: false
    }
]
